import { memo, useState, useMemo } from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import {
  EligibleEnrollment,
  useGetEligibleEnrollmentsQuery,
  useIssueCertificatesBatchMutation,
  useIssueCertificateMutation,
} from '@/entities/Certificates';
import { AppButton } from '@/shared/ui/AppButton';
import { useToast } from '@/shared/lib/hooks/useToast';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { AppImage } from '@/shared/ui/AppImage';
import { Svg } from '@/shared/ui/Svg';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import CertificatesIcon from '@/shared/assets/icons/sidebar/certificates.svg';
import styles from './GenerateCertificateModal.module.scss';

interface GenerateCertificateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: number;
  courseTitle: string;
  intakeId: number | null;
  groupNumber: number | null;
  onSuccess?: () => void;
}

export const GenerateCertificateModal = memo(
  ({
    isOpen,
    onOpenChange,
    courseId,
    courseTitle,
    intakeId,
    groupNumber,
    onSuccess,
  }: GenerateCertificateModalProps) => {
    const { toast } = useToast();
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [issueCertificatesBatch, { isLoading: isIssuing }] = useIssueCertificatesBatchMutation();
    const [issueCertificate, { isLoading: isIssuingOne }] = useIssueCertificateMutation();
    const [issuingEnrollmentId, setIssuingEnrollmentId] = useState<number | null>(null);

    const { data, isLoading } = useGetEligibleEnrollmentsQuery(
      {
        courseId,
        ...(intakeId !== null && { intakeId }),
        ...(groupNumber !== null && { groupNumber }),
      },
      { skip: !isOpen || !intakeId },
    );

    const enrollments = useMemo(() => data?.data ?? [], [data?.data]);

    const allSelected = enrollments.length > 0 && selectedIds.size === enrollments.length;
    const someSelected = selectedIds.size > 0;

    const handleToggleAll = () => {
      if (allSelected) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(enrollments.map((e) => e.enrollmentId)));
      }
    };

    const handleToggleOne = (enrollmentId: number) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(enrollmentId)) {
          next.delete(enrollmentId);
        } else {
          next.add(enrollmentId);
        }
        return next;
      });
    };

    const handleClose = () => {
      setSelectedIds(new Set());
      onOpenChange(false);
    };

    const handleGenerate = async () => {
      if (!someSelected || intakeId === null) return;
      try {
        await issueCertificatesBatch({
          enrollmentIds: Array.from(selectedIds),
          intakeId,
        }).unwrap();
        toast({
          title: 'Success',
          description: 'Certificates issued successfully',
          color: 'success',
        });
        onSuccess?.();
        handleClose();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message ?? 'Failed to issue certificates',
          color: 'danger',
        });
      }
    };

    const handleIssueOne = async (enrollmentId: number) => {
      if (intakeId === null) return;
      setIssuingEnrollmentId(enrollmentId);
      try {
        await issueCertificate({ enrollmentId, intakeId }).unwrap();
        toast({
          title: 'Success',
          description: 'Certificate issued successfully',
          color: 'success',
        });
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message ?? 'Failed to issue certificate',
          color: 'danger',
        });
      } finally {
        setIssuingEnrollmentId(null);
      }
    };

    const subtitle = useMemo(() => {
      const parts = [courseTitle];
      if (intakeId !== null || groupNumber !== null) {
        const extra: string[] = [];
        if (groupNumber !== null) extra.push(`Group ${groupNumber}`);
        if (intakeId !== null) extra.push(`Intake ${intakeId}`);
        if (extra.length) parts.push(extra.join(' · '));
      }
      return parts.join(' ');
    }, [courseTitle, intakeId, groupNumber]);

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => !open && handleClose()}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[560px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col items-start gap-0">
                <span className={styles.title}>Generate a certificate</span>
                <span className={styles.subtitle}>{subtitle}</span>
              </ModalHeader>
              <ModalBody className="p-0 overflow-hidden">
                {isLoading ? (
                  <div className="py-12 text-center text-grey-secondary">Loading...</div>
                ) : enrollments.length === 0 ? (
                  <div className="py-12 text-center text-grey-secondary">
                    No students eligible for certificate in this intake
                    {groupNumber !== null ? ` / group ${groupNumber}` : ''}.
                  </div>
                ) : (
                  <div className="rounded-lg border border-grey-border overflow-hidden">
                    <div className={styles.listHeader}>
                      <div>
                        <IndeterminateCheckbox
                          uniqueKey="generate-cert-all"
                          name="generate-cert-all"
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={handleToggleAll}
                        />
                      </div>
                      <span>ID</span>
                      <span>User</span>
                      <span className={styles.actionHeader}>Issue</span>
                    </div>
                    <div className={styles.listBody}>
                      {enrollments.map((item: EligibleEnrollment) => (
                        <div key={item.enrollmentId} className={styles.listRow} role="row">
                          <div>
                            <IndeterminateCheckbox
                              uniqueKey={`generate-cert-${item.enrollmentId}`}
                              name="generate-cert"
                              checked={selectedIds.has(item.enrollmentId)}
                              onChange={() => handleToggleOne(item.enrollmentId)}
                            />
                          </div>
                          <div className={styles.idCell}>{item.enrollmentId}</div>
                          <div className={styles.userCell}>
                            {item.student.avatar ? (
                              <AppImage
                                src={item.student.avatar}
                                alt={item.student.username}
                                className={styles.avatar}
                              />
                            ) : (
                              <div className={styles.avatarPlaceholder}>
                                {item.student.username?.charAt(0) ?? '?'}
                              </div>
                            )}
                            <span className={styles.nameCell}>{item.student.username}</span>
                          </div>
                          <div className={styles.actionCell}>
                            <Button
                              isIconOnly
                              variant="light"
                              size="sm"
                              aria-label="Issue certificate"
                              onPress={() => handleIssueOne(item.enrollmentId)}
                              isDisabled={isIssuingOne}
                              isLoading={issuingEnrollmentId === item.enrollmentId}
                            >
                              <Svg Icon={CertificatesIcon} width={20} height={20} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="flex justify-center items-center gap-4 p-0 pt-4">
                <AppButton className="w-full" color="white" onPress={handleClose}>
                  Cancel
                </AppButton>
                <AppButton
                  color="black"
                  className="w-full"
                  onPress={handleGenerate}
                  disabled={!someSelected || isIssuing}
                  isLoading={isIssuing}
                >
                  Generate
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

GenerateCertificateModal.displayName = 'GenerateCertificateModal';
